import { useEffect, useMemo, useState } from "react";

const uid = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const splitCode = (code) => String(code || "").split(".").map((x) => parseInt(x, 10) || 0);

const compareCodes = (a, b) => {
  const aa = splitCode(a);
  const bb = splitCode(b);
  const L = Math.max(aa.length, bb.length);

  for (let i = 0; i < L; i++) {
    const x = aa[i] ?? -1;
    const y = bb[i] ?? -1;
    if (x !== y) return x - y;
  }

  return 0;
};

const normalizeAccountsFromApi = (payload) => {
  const arr = payload?.accounts || payload?.data || payload || [];

  if (!Array.isArray(arr)) return [];

  return arr
    .map((a) => ({
      id: String(a.id ?? a.pk ?? uid()),
      parentId: a.parentId === null || a.parentId === undefined ? null : String(a.parentId ?? a.padre_id ?? a.padre),
      code: String(a.code ?? a.codigo ?? ""),
      name: String(a.name ?? a.nombre ?? ""),
      group: String(a.group ?? a.grupo ?? ""),
      type: String(a.type ?? a.tipo ?? ""),
      nature: String(a.nature ?? a.naturaleza ?? ""),
      isMovement: Boolean(a.isMovement ?? a.es_movimiento ?? false),
      isSystem: Boolean(a.isSystem ?? a.es_sistema ?? false),
      visible: Boolean(a.visible ?? a.visible_usuario ?? true),
      editable: Boolean(a.editable ?? a.permite_editar ?? true),
      deletable: Boolean(a.deletable ?? a.permite_eliminar ?? true),
      active: Boolean(a.active ?? a.activa ?? true),
      balance: Number(a.balance ?? a.saldo ?? 0) || 0,
    }))
    .filter((a) => a.code && a.name);
};

const toNumber = (v) => {
  const s = String(v ?? "").trim().replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const getErrorMessage = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  const error = data?.error;

  if (!error) return fallback;

  if (typeof error === "string") return error;

  if (Array.isArray(error)) return error.join(" ");

  if (typeof error === "object") {
    return Object.entries(error)
      .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : String(v)}`)
      .join(" | ");
  }

  return fallback;
};

export default function useAccountsController(navigate) {
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [expandedIds, setExpandedIds] = useState([]);
  const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuAccountId, setMenuAccountId] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("add");
  const [editorParentId, setEditorParentId] = useState(null);
  const [editorAccountId, setEditorAccountId] = useState(null);

  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("");
  const [formBalance, setFormBalance] = useState("0.00");

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteAccountId, setDeleteAccountId] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const closeSnackbar = () => setSnackbarOpen(false);

  const childrenByParent = useMemo(() => {
    const m = new Map();

    for (const a of accounts) {
      const key = a.parentId ?? "__root__";

      if (!m.has(key)) m.set(key, []);

      m.get(key).push(a);
    }

    for (const [k, arr] of m.entries()) {
      arr.sort((x, y) => compareCodes(x.code, y.code));
      m.set(k, arr);
    }

    return m;
  }, [accounts]);

  const hasChildren = (id) => (childrenByParent.get(String(id)) || []).length > 0;

  const allExpandableIds = useMemo(() => {
    const ids = [];

    for (const a of accounts) {
      if (hasChildren(a.id)) ids.push(a.id);
    }

    return ids;
  }, [accounts, childrenByParent]);

  const isAllExpanded = allExpandableIds.length > 0 && expandedIds.length === allExpandableIds.length;

  const toggleNode = (id) => {
    setExpandedIds((prev) => {
      const s = new Set(prev);

      if (s.has(String(id))) s.delete(String(id));
      else s.add(String(id));

      return Array.from(s);
    });
  };

  const toggleExpandAll = () => {
    if (isAllExpanded) setExpandedIds([]);
    else setExpandedIds(allExpandableIds);
  };

  const openMenuFor = (e, accountId) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuAccountId(String(accountId));
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuAccountId(null);
  };

  const selectedAccount = useMemo(() => {
    if (!menuAccountId) return null;
    return accounts.find((a) => a.id === String(menuAccountId)) || null;
  }, [menuAccountId, accounts]);

  const canEditSelected = !!selectedAccount && !!selectedAccount.editable;
  const canDeleteSelected = !!selectedAccount && !!selectedAccount.parentId && !!selectedAccount.deletable && !hasChildren(selectedAccount.id);
  const canAddToSelected = !!selectedAccount && !selectedAccount.isMovement;

  const computeNextChildCodePreview = (parentId) => {
    const parent = accounts.find((a) => a.id === String(parentId));

    if (!parent) return "";

    const kids = (childrenByParent.get(String(parentId)) || []).map((k) => {
      const parts = splitCode(k.code);
      return parts[parts.length - 1] || 0;
    });

    const max = kids.length ? Math.max(...kids) : 0;
    return `${parent.code}.${String(max + 1).padStart(2, "0")}`;
  };

  const getAccessToken = () => localStorage.getItem("access") || "";

  const buildAuthHeaders = (extra = {}) => {
    const token = getAccessToken();
    const headers = { ...extra };

    if (token) headers.Authorization = `Bearer ${token}`;

    return headers;
  };

  const ensureLogged = async () => {
    try {
      const token = getAccessToken();

      if (!token) {
        navigate("/login");
        return false;
      }

      const r = await fetch("/sistema/get-user-info/", {
        method: "GET",
        headers: buildAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        return false;
      }

      return true;
    } catch {
      return true;
    }
  };

  const toast = (type, message) => {
    setSnackbarType(type);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const fetchAccounts = async () => {
    setLoading(true);

    try {
      const r = await fetch("/cuentas/listar/", {
        method: "GET",
        headers: buildAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        setLoading(false);
        navigate("/login");
        return;
      }

      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        setAccounts(normalizeAccountsFromApi(d));
      }
    } catch {
      toast("error", "No se pudo cargar el plan de cuentas");
    }

    setLoading(false);
  };

  useEffect(() => {
    const init = async () => {
      const ok = await ensureLogged();

      if (!ok) return;

      await fetchAccounts();
    };

    init();
  }, [navigate]);

  const openAddDialog = (parentId) => {
    const parent = accounts.find((a) => a.id === String(parentId));

    if (!parent) return;

    if (parent.isMovement) {
      toast("warning", "No puedes agregar subcuentas dentro de una cuenta de movimiento.");
      return;
    }

    setEditorMode("add");
    setEditorParentId(String(parentId));
    setEditorAccountId(null);
    setFormCode(computeNextChildCodePreview(String(parentId)));
    setFormName("");
    setFormType("");
    setFormBalance("0.00");
    setEditorOpen(true);

    setExpandedIds((prev) => {
      const s = new Set(prev);
      s.add(String(parentId));
      return Array.from(s);
    });
  };

  const openEditDialog = (accountId) => {
    const a = accounts.find((x) => x.id === String(accountId));

    if (!a) return;

    if (!a.editable) {
      toast("warning", "Esta cuenta está protegida por el sistema.");
      return;
    }

    setEditorMode("edit");
    setEditorParentId(a.parentId ?? null);
    setEditorAccountId(a.id);
    setFormCode(a.code || "");
    setFormName(a.name || "");
    setFormType(String(a.type || "").trim());
    setFormBalance(String(a.balance ?? 0));
    setEditorOpen(true);
  };

  const openDeleteDialog = (accountId) => {
    const a = accounts.find((x) => x.id === String(accountId));

    if (!a) return;

    if (!a.deletable || hasChildren(a.id)) {
      toast("warning", "Esta cuenta no se puede eliminar.");
      return;
    }

    setDeleteAccountId(String(accountId));
    setDeleteOpen(true);
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorMode("add");
    setEditorParentId(null);
    setEditorAccountId(null);
    setFormCode("");
    setFormName("");
    setFormType("");
    setFormBalance("0.00");
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteAccountId(null);
  };

  const saveEditor = async () => {
    const name = String(formName || "").trim();

    if (!name) {
      toast("warning", "Ingresa el nombre de la cuenta.");
      return;
    }

    const saldo = toNumber(formBalance);
    const tipo = String(formType || "").trim();

    if (editorMode === "add") {
      try {
        const r = await fetch("/cuentas/crear/", {
          method: "POST",
          headers: buildAuthHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({
            padre_id: editorParentId,
            nombre: name,
            saldo,
            tipo: tipo || null,
            es_movimiento: true,
            visible_usuario: true,
          }),
        });

        if (r.status === 401 || r.status === 403) {
          navigate("/login");
          return;
        }

        if (r.ok) {
          toast("success", "Cuenta creada correctamente");
          closeEditor();
          await fetchAccounts();
          return;
        }

        toast("error", await getErrorMessage(r, "No se pudo crear la cuenta"));
      } catch {
        toast("error", "No se pudo crear la cuenta");
      }

      return;
    }

    try {
      const r = await fetch(`/cuentas/editar/${editorAccountId}/`, {
        method: "PUT",
        headers: buildAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          nombre: name,
          tipo: typeDisabled ? undefined : tipo || null,
        }),
      });

      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        return;
      }

      if (r.ok) {
        toast("success", "Cuenta modificada correctamente");
        closeEditor();
        await fetchAccounts();
        return;
      }

      toast("error", await getErrorMessage(r, "No se pudo modificar la cuenta"));
    } catch {
      toast("error", "No se pudo modificar la cuenta");
    }
  };

  const confirmDelete = async () => {
    if (!deleteAccountId) return;

    const target = accounts.find((a) => a.id === String(deleteAccountId));

    if (!target || !target.parentId || !target.deletable || hasChildren(target.id)) {
      closeDelete();
      return;
    }

    try {
      const r = await fetch(`/cuentas/eliminar/${deleteAccountId}/`, {
        method: "DELETE",
        headers: buildAuthHeaders(),
      });

      if (r.status === 401 || r.status === 403) {
        navigate("/login");
        return;
      }

      if (r.ok) {
        toast("success", "Cuenta eliminada correctamente");
        closeDelete();
        await fetchAccounts();
        return;
      }

      toast("error", await getErrorMessage(r, "No se pudo eliminar la cuenta"));
    } catch {
      toast("error", "No se pudo eliminar la cuenta");
    }
  };

  const balanceDisabled = useMemo(() => {
    if (!editorOpen) return false;
    if (editorMode !== "edit") return true;
    if (!editorAccountId) return true;
    return true;
  }, [editorOpen, editorMode, editorAccountId]);

  const typeDisabled = useMemo(() => {
    if (!editorOpen) return false;
    if (editorMode !== "edit") return false;
    if (!editorAccountId) return false;
    return hasChildren(editorAccountId);
  }, [editorOpen, editorMode, editorAccountId, childrenByParent]);

  const displayBalanceById = useMemo(() => {
    const byId = new Map();
    const byParent = childrenByParent;

    const ownBalance = (id) => {
      const a = accounts.find((x) => x.id === String(id));
      return Number(a?.balance || 0) || 0;
    };

    const calc = (id) => {
      const kids = byParent.get(String(id)) || [];

      if (!kids.length) {
        const b = ownBalance(id);
        byId.set(String(id), b);
        return b;
      }

      let total = 0;

      for (const k of kids) total += calc(k.id);

      byId.set(String(id), total);

      return total;
    };

    const roots = byParent.get("__root__") || [];

    for (const r of roots) calc(r.id);

    return byId;
  }, [accounts, childrenByParent]);

  return {
    open,
    setOpen,
    submenuOpen,
    setSubmenuOpen,
    accounts,
    loading,
    expandedSet,
    isAllExpanded,
    childrenByParent,
    hasChildren,
    toggleNode,
    toggleExpandAll,
    menuAnchorEl,
    openMenuFor,
    closeMenu,
    selectedAccount,
    canDeleteSelected,
    canEditSelected,
    canAddToSelected,
    openAddDialog,
    openEditDialog,
    openDeleteDialog,
    editorOpen,
    editorMode,
    formCode,
    formName,
    setFormName,
    formType,
    setFormType,
    formBalance,
    setFormBalance,
    balanceDisabled,
    typeDisabled,
    closeEditor,
    saveEditor,
    deleteOpen,
    closeDelete,
    confirmDelete,
    snackbarOpen,
    snackbarType,
    snackbarMessage,
    closeSnackbar,
    displayBalanceById,
  };
}